import { MAX_RESPONSE_LENGTH } from '@/shared-question-model';

import { createThreadBehaviour } from './shared-responder-v2-core-thread-behaviour';
import type { ResponderDraftEvent } from './shared-responder-v2-core-thread-behaviour';
import { THREAD_KINDS } from './shared-responder-v2-core-thread.consts';
import type { ResponderAnswer } from './shared-responder-v2-core-thread.types';

type OpenEndedDraft = Extract<ResponderAnswer, { kind: 'text' }>;

export function emptyOpenEndedDraft(): OpenEndedDraft {
  return { kind: 'text', text: '', transcript: null };
}

export function reduceOpenEndedDraft(
  draft: ResponderAnswer | null,
  event: ResponderDraftEvent
): ResponderAnswer | null {
  if (event.type !== 'textChanged' && event.type !== 'transcriptChanged') {
    return draft;
  }

  const base = draft?.kind === 'text' ? draft : emptyOpenEndedDraft();

  if (event.type === 'textChanged') {
    return { ...base, text: event.text };
  }

  return { ...base, transcript: event.transcript };
}

export interface AnswerLengthState {
  readonly length: number;
  readonly limit: number;
  readonly isOverLimit: boolean;
}

export function resolveAnswerLengthState(text: string): AnswerLengthState {
  const length = Array.from(text).length;

  return {
    length,
    limit: MAX_RESPONSE_LENGTH,
    isOverLimit: length > MAX_RESPONSE_LENGTH,
  };
}

function resolveSentText(draft: OpenEndedDraft): string {
  return draft.text.trim() === '' ? (draft.transcript ?? '') : draft.text;
}

export function isOpenEndedDraftComplete(
  draft: ResponderAnswer | null
): boolean {
  if (draft?.kind !== 'text') {
    return false;
  }

  const sentText = resolveSentText(draft);

  return (
    sentText.trim().length > 0 &&
    !resolveAnswerLengthState(sentText).isOverLimit
  );
}

export const OPEN_ENDED_THREAD_BEHAVIOUR = createThreadBehaviour({
  kind: THREAD_KINDS.OpenEnded,
  // The ticket's single exception: a project in text mode otherwise suppresses
  // voice recording when it contains a URL or Task, but an open-ended question
  // explicitly set to voice keeps recording regardless.
  recordsVoiceWhenUrlPresent: true,
  answersInComposer: true,
  surfaceInputIsComposer: true,
  emptyDraft: emptyOpenEndedDraft,
  reduceDraft: reduceOpenEndedDraft,
  isDraftComplete: isOpenEndedDraftComplete,
  // Always shown so a voice responder has an explicit way to signal they have
  // finished speaking, instead of relying on silence detection alone.
  showsContinueButton: () => true,
});
